import { Node, mergeAttributes } from "@tiptap/core";

// Detects YouTube, Instagram, TikTok URLs and returns {embed, provider}
// provider determines aspect ratio: "vertical" (9:16) or "horizontal" (16:9)
export function toEmbedInfo(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    // YouTube
    if (host === "youtube.com" || host === "m.youtube.com") {
      const v = u.searchParams.get("v");
      if (v) return { embed: `https://www.youtube.com/embed/${v}`, provider: "youtube" };
      const shortMatch = u.pathname.match(/^\/shorts\/([^/]+)/);
      if (shortMatch) return { embed: `https://www.youtube.com/embed/${shortMatch[1]}`, provider: "youtube_short" };
    }
    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "");
      if (id) return { embed: `https://www.youtube.com/embed/${id}`, provider: "youtube" };
    }
    // Instagram reel/post — reels are vertical, posts may vary
    if (host === "instagram.com") {
      const m = u.pathname.match(/^\/(p|reel|tv)\/([^/]+)/);
      if (m) {
        const kind = m[1];
        return {
          embed: `https://www.instagram.com/${kind}/${m[2]}/embed/`,
          provider: kind === "reel" ? "instagram_reel" : "instagram_post",
        };
      }
    }
    // TikTok — always vertical
    if (host === "tiktok.com") {
      const m = u.pathname.match(/\/video\/(\d+)/);
      if (m) return { embed: `https://www.tiktok.com/embed/v2/${m[1]}`, provider: "tiktok" };
    }
    // Already an embed
    if (url.includes("/embed/")) return { embed: url, provider: "other" };
    return { embed: url, provider: "other" };
  } catch (_) {
    return { embed: url, provider: "other" };
  }
}

// Legacy export — kept for backwards compatibility (returns only URL)
export function toEmbedUrl(url) {
  const r = toEmbedInfo(url);
  return r ? r.embed : null;
}

const VERTICAL_PROVIDERS = new Set(["tiktok", "instagram_reel", "youtube_short"]);

export const EmbedIframe = Node.create({
  name: "embedIframe",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      provider: { default: "other" },
    };
  },

  parseHTML() {
    return [{ tag: "iframe[data-embed]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const provider = HTMLAttributes.provider || "other";
    const isVertical = VERTICAL_PROVIDERS.has(provider);
    const wrapperClass = `popu-embed-wrapper popu-embed-${provider} ${isVertical ? "popu-embed-vertical" : "popu-embed-horizontal"}`;
    return [
      "div",
      { class: wrapperClass, "data-provider": provider },
      [
        "iframe",
        mergeAttributes(HTMLAttributes, {
          "data-embed": "true",
          frameborder: "0",
          allow: "autoplay; encrypted-media; picture-in-picture; fullscreen",
          allowfullscreen: "true",
        }),
      ],
    ];
  },

  addCommands() {
    return {
      setEmbed: (src) => ({ commands }) => {
        const info = toEmbedInfo(src);
        if (!info) return false;
        return commands.insertContent({
          type: "embedIframe",
          attrs: { src: info.embed, provider: info.provider },
        });
      },
    };
  },
});
